import { getAccessToken, getRefreshPromise } from "@/lib/api"

const ragBaseURL = (import.meta.env.VITE_RAG_API_URL ?? "").replace(/\/$/, "")

/** #53 HARD RULE (same shape as #97): SME feedback is a dev/local-only affordance for expert
 * review — these Vite build modes may render it; any other mode (prod included) never mounts
 * the control. The backend endpoint independently 404s outside dev, so even a mis-built
 * bundle could not submit from prod. */
const SME_FEEDBACK_ALLOWED_MODES = ["development", "dev", "local", "test"]
export const smeFeedbackAllowed = SME_FEEDBACK_ALLOWED_MODES.includes(import.meta.env.MODE)

export type SmeVerdict = "correct" | "partly" | "incorrect"

export interface SmeFeedbackResult {
  verdict: SmeVerdict
  /** Which Langfuse traces received the score ("answer", "deep_dive"). */
  scored_traces: string[]
}

/**
 * Submit the SME's verdict + comment for an assistant message. The backend attaches it to the
 * message's Langfuse traces as `sme-feedback` scores (answer trace + dive trace when present),
 * so reviewed runs are filterable in Langfuse with full spans and metadata.
 */
export async function submitSmeFeedback(
  chatId: string,
  assistantTimestamp: string,
  verdict: SmeVerdict,
  comment: string
): Promise<SmeFeedbackResult> {
  const path = `/chat/${encodeURIComponent(chatId)}/sme-feedback`
  const url = ragBaseURL ? `${ragBaseURL}${path}` : path
  const payload = JSON.stringify({
    assistant_timestamp: assistantTimestamp,
    verdict,
    comment,
  })

  const send = (token: string | null) => {
    const headers: Record<string, string> = { "Content-Type": "application/json" }
    if (token) headers.Authorization = `Bearer ${token}`
    return fetch(url, { method: "POST", headers, body: payload })
  }

  let response = await send(getAccessToken())
  if (response.status === 401) {
    const newToken = await getRefreshPromise()
    if (!newToken) throw new Error("Unauthorized")
    response = await send(newToken)
  }

  if (!response.ok) {
    let message = `Feedback submit failed (${response.status})`
    try {
      const errBody = (await response.json()) as { error?: string }
      if (errBody.error) message = errBody.error
    } catch {
      // ignore parse errors
    }
    throw new Error(message)
  }

  return (await response.json()) as SmeFeedbackResult
}
